import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { useI18n } from '../contexts/I18nContext';

const COPY = {
  de: {
    title: 'Diese Website verwendet Cookies',
    body: 'Wir verwenden Cookies, um deine Erfahrung auf leendr zu verbessern. Notwendige Cookies sind für die Funktion der Plattform erforderlich (z. B. Anmeldung). Andere Kategorien kannst du individuell entscheiden. Deine Einwilligung kannst du jederzeit im Footer widerrufen.',
    necessary: 'Notwendig', necessary_desc: 'Auth/Session, Sicherheitsfunktionen. Immer aktiv.',
    statistics: 'Statistik', statistics_desc: 'Anonyme Nutzungsstatistiken, um Leendr zu verbessern.',
    marketing: 'Marketing', marketing_desc: 'Personalisierte Empfehlungen und Kampagnen-Messung.',
    comfort: 'Komfort', comfort_desc: 'Speichert z. B. kürzlich angesehene Inserate und Einstellungen.',
    accept_all: 'Alle akzeptieren',
    only_necessary: 'Nur notwendige',
    save_selection: 'Auswahl speichern',
    settings: 'Einstellungen',
    privacy: 'Datenschutz',
    imprint: 'Impressum',
  },
  en: {
    title: 'This site uses cookies',
    body: 'We use cookies to improve your experience on leendr. Necessary cookies are required for the platform to function (e.g. sign-in). Other categories are up to you. You can revoke your consent anytime from the footer.',
    necessary: 'Necessary', necessary_desc: 'Auth/session, security features. Always on.',
    statistics: 'Statistics', statistics_desc: 'Anonymous usage stats to improve Leendr.',
    marketing: 'Marketing', marketing_desc: 'Personalized recommendations and campaign measurement.',
    comfort: 'Comfort', comfort_desc: 'Remembers recently viewed listings and preferences.',
    accept_all: 'Accept all',
    only_necessary: 'Only necessary',
    save_selection: 'Save selection',
    settings: 'Settings',
    privacy: 'Privacy',
    imprint: 'Imprint',
  },
};

function Toggle({ on, onChange, disabled }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${on ? 'bg-[#534AB7]' : 'bg-[#D8D5F5]'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-[18px]' : 'translate-x-[2px]'} mt-[2px]`} />
    </button>
  );
}

export default function CookieConsentBanner() {
  const { showBanner, acceptAll, acceptOnlyNecessary, save } = useCookieConsent();
  const { lang } = useI18n();
  const t = COPY[lang] || COPY.de;
  const [expanded, setExpanded] = useState(false);
  const [sel, setSel] = useState({ statistics: false, marketing: false, comfort: false });

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[9999] px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="max-w-4xl mx-auto bg-white border border-[#E5E5E5] shadow-2xl rounded-lg overflow-hidden">
        <div className="p-5 sm:p-6">
          <h2 id="cookie-consent-title" className="text-base font-bold text-[#11100F] mb-1">{t.title} 🍪</h2>
          <p className="text-sm text-[#6B6B6B] leading-relaxed">
            {t.body}{' '}
            <Link to="/datenschutz" className="text-[#534AB7] font-semibold underline hover:opacity-80">{t.privacy}</Link>
            {' · '}
            <Link to="/impressum" className="text-[#534AB7] font-semibold underline hover:opacity-80">{t.imprint}</Link>
          </p>

          {expanded && (
            <div className="mt-4 space-y-3 border-t border-[#F0F0F0] pt-4" data-testid="cookie-consent-categories">
              {[
                { k: 'necessary', on: true, disabled: true },
                { k: 'statistics', on: sel.statistics, disabled: false },
                { k: 'marketing', on: sel.marketing, disabled: false },
                { k: 'comfort', on: sel.comfort, disabled: false },
              ].map(({ k, on, disabled }) => (
                <div key={k} className="flex items-start gap-3" data-testid={`cookie-category-${k}`}>
                  <div className="pt-0.5"><Toggle on={on} disabled={disabled} onChange={(v) => setSel(s => ({ ...s, [k]: v }))} /></div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-[#11100F]">{t[k]}{disabled && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#6B6B6B]">Pflicht</span>}</p>
                    <p className="text-xs text-[#6B6B6B] leading-relaxed">{t[`${k}_desc`]}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="cookie-accept-necessary"
              variant="outline"
              onClick={acceptOnlyNecessary}
              className="rounded-md border-[#E5E5E5] text-[#11100F] font-bold order-2 sm:order-1"
            >
              {t.only_necessary}
            </Button>
            {expanded ? (
              <Button
                data-testid="cookie-save-selection"
                onClick={() => save({ necessary: true, ...sel })}
                className="rounded-md bg-[#11100F] text-white font-bold hover:bg-[#2A2826] sm:order-2"
              >
                {t.save_selection}
              </Button>
            ) : (
              <Button
                data-testid="cookie-open-settings"
                variant="outline"
                onClick={() => setExpanded(true)}
                className="rounded-md border-[#E5E5E5] text-[#11100F] font-bold sm:order-2"
              >
                {t.settings}
              </Button>
            )}
            <Button
              data-testid="cookie-accept-all"
              onClick={acceptAll}
              className="rounded-md bg-[#534AB7] text-white font-bold hover:bg-[#3E3793] order-1 sm:order-3"
            >
              {t.accept_all}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
